import type { CoinJackpotTier } from "../rules/symbols";

/**
 * The offline server's numbers. PLACEHOLDERS: they make the offline rounds behave like a hold-and-win game, set for the
 * demo — a gem's comet in most spins (the user, 2026-10-11: the offline return does not matter for now), so the pots
 * fill often and a full pot's draw starts the bonus every few spins: the base game returns well over the bet; the buys
 * return roughly 95 % (`pnpm sim:rtp` measures them). They are not a certified model, and no player-facing text states a probability or an RTP.
 * Each line is an equal bet (decision 56): an active line's four cells covered start the bonus about once in fifty-odd
 * spins, a bonus worth about fifty line bets on average (coins mostly a fraction of a line's bet, rare jackpots). The
 * design facts the screen shows — the jackpots' multiples, the buy prices, the respin count — are the rules'
 * (`rules/symbols.ts`, the purchase offers).
 */

/** A base cell's symbol, by weight. */
export const BASE_SYMBOL_WEIGHTS: readonly { value: "blank" | "coin" | "mystery" | "mysteryJackpot" | CoinJackpotTier; weight: number }[] = [
  { value: "blank", weight: 488 },
  { value: "coin", weight: 247 },
  { value: "mystery", weight: 12 },
  { value: "mysteryJackpot", weight: 4 },
  { value: "mini", weight: 14 },
  { value: "minor", weight: 6 },
  { value: "major", weight: 3 },
];

/** A coin's value in tenths of the bet, by weight (0.2× to 20×; bets are whole tenths of a unit, so values stay whole). */
export const COIN_VALUE_TENTHS: readonly { value: number; weight: number }[] = [
  { value: 2, weight: 16 },
  { value: 4, weight: 20 },
  { value: 6, weight: 16 },
  { value: 8, weight: 13 },
  { value: 10, weight: 11 },
  { value: 15, weight: 9 },
  { value: 20, weight: 6 },
  { value: 30, weight: 4 },
  { value: 50, weight: 3 },
  { value: 100, weight: 1.5 },
  { value: 200, weight: 0.5 },
];

/**
 * A landed base coin in an active line's row carries a pot's gem this often (the pot picked evenly; a full pot still
 * takes its comet): a comet in about four spins in ten on one line, eight in ten on three.
 */
export const MARK_CHANCE = 0.35;

/** A full pot starts the bonus at random: on each spin while one is full, this often (it waits about five spins). */
export const POT_TRIGGER_CHANCE = 0.2;

/** The respins the bonus starts with, and the count every new symbol resets the meter to. */
export const RESPINS = 3;

/** A respin: each empty cell in play lands a bonus symbol this often (lower on the grown board). */
export const RESPIN_LAND_CHANCE = { base: 0.08, grown: 0.065 } as const;

/** What a respin lands, by weight. */
export const RESPIN_SYMBOL_WEIGHTS: readonly { value: "coin" | "collect" | "mystery" | "mysteryJackpot" | CoinJackpotTier; weight: number }[] = [
  { value: "coin", weight: 880 },
  { value: "collect", weight: 35 },
  { value: "mystery", weight: 40 },
  { value: "mysteryJackpot", weight: 15 },
  { value: "mini", weight: 30 },
  { value: "minor", weight: 12 },
  { value: "major", weight: 3 },
];

/** What a mystery jackpot reveals, by weight: a jackpot coin, never a coin. */
export const MYSTERY_JACKPOT_REVEAL_WEIGHTS: readonly { value: CoinJackpotTier; weight: number }[] = [
  { value: "mini", weight: 70 },
  { value: "minor", weight: 24 },
  { value: "major", weight: 6 },
];

/** What a mystery coin reveals, by weight. */
export const MYSTERY_REVEAL_WEIGHTS: readonly { value: "coin" | CoinJackpotTier; weight: number }[] = [
  { value: "coin", weight: 85 },
  { value: "mini", weight: 10 },
  { value: "minor", weight: 4 },
  { value: "major", weight: 1 },
];

/** The MULTI booster: how many ×2 drops a bonus throws (1–8), and how often a respin erupts while some are left. */
export const MULTI_DROPS = { min: 1, max: 8, eruptChance: 0.35, perEruption: { min: 1, max: 3 } } as const;

/** The Madness buy: MULTI erupts on every respin, this many drops each. */
export const MADNESS_DROPS_PER_RESPIN = { min: 2, max: 3 } as const;

/** A round's win is capped at this many bets. */
export const MAX_WIN_MULTIPLIER = 5000;

/** The bonus buys: their ids (the server contract), their price in bets, and the boosters they start with. */
export const BONUS_BUYS = {
  bonus_random: { price: 75, boosters: "random" },
  bonus_trio: { price: 95, boosters: "all" },
  bonus_madness: { price: 170, boosters: "madness" },
} as const;

export type BonusBuyId = keyof typeof BONUS_BUYS;

export function isBonusBuyId(id: string): id is BonusBuyId {
  return Object.hasOwn(BONUS_BUYS, id);
}

/** The big-win tiers, in bet multiples: the offline boot's settings and the offline server's `present_big_win` alike. */
export const BIG_WIN_TIERS = [
  { levelIdentifier: "big", minWinMultiplier: 20 },
  { levelIdentifier: "mega", minWinMultiplier: 50 },
  { levelIdentifier: "epic", minWinMultiplier: 150 },
] as const;
